import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import '../css/Sidebar.css';
import '../css/Navbar.css';
import '../css/BugReport.css';
import BugReportModal from '../modal/BugReportModal';
import BugReportView from '../components/BugReportView';
import { FaBug, FaPlus, FaCheckCircle, FaRegCircle, FaLock, FaClock, FaExclamationTriangle, FaSearch, FaFilter } from "react-icons/fa";
import { authAPI, bugReportsAPI } from '../api/api';


const initialTickets = [];

const statusCell = (status) => {
  if (status === 'Megoldva') {
    return <span className="bugreport-status-solved"><FaCheckCircle />Megoldva</span>;
  }
  if (status === 'Lezárva') {
    return <span className="bugreport-status-closed"><FaLock />Lezárva</span>;
  }
  if (status === 'Folyamatban') {
    return <span className="bugreport-status-in-progress"><FaClock />Folyamatban</span>;
  }
  return <span className="bugreport-status-open"><FaExclamationTriangle />Bejelentett</span>;
};

const BugReport = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [tickets, setTickets] = useState(initialTickets);
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Load bug reports on component mount
  useEffect(() => {
    testBackendConnection();
  }, []);

  const testBackendConnection = async () => {
    try {
      setLoading(true);
      setError('');
      

      const apiUrl = process.env.REACT_APP_API_BASE_URL;


      try {
        const basicResponse = await fetch(`${apiUrl.replace('/api', '')}`, { 
          method: 'HEAD',
          mode: 'no-cors' // This will fail if the server is not reachable
        });

      } catch (basicError) {

        // Continue with API tests anyway
      }
      
      // Test 1: Try to access a known working endpoint first

      const token = localStorage.getItem('authToken');
      const companiesResponse = await fetch(`${apiUrl}/get-companies`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (companiesResponse.ok) {
        const companiesText = await companiesResponse.text();

        
        try {
          const companiesData = JSON.parse(companiesText);


          await loadBugReports();
        } catch (parseError) {
          console.error('Failed to parse companies response as JSON:', parseError);
          setError(`Backend returned invalid JSON for companies endpoint. Response starts with: ${companiesText.substring(0, 100)}...`);
        }
      } else {
        const errorText = await companiesResponse.text();

        setError(`Backend responded with status: ${companiesResponse.status}. Response: ${errorText.substring(0, 200)}...`);
      }
    } catch (err) {
      console.error('Backend connection test failed:', err);
      if (err.message.includes('Failed to fetch')) {
        setError('Nem sikerült kapcsolódni a szerverhez. Ellenőrizd, hogy a backend fut-e a 8080-es porton.');
      } else {
        setError('Hiba a szerverrel való kapcsolatban: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadBugReports = async () => {
    try {
      // Get the current user's company ID
      const currentUser = authAPI.getCurrentUser();
      const clientId = currentUser?.client_id || currentUser?.company_id || 1; // Try client_id first, then company_id, default to 1
      
      const data = await bugReportsAPI.getBugReports(clientId);
      
      // Transform backend data to frontend format
      const transformedTickets = data.map(ticket => ({
        id: ticket.id,
        subject: ticket.subject,
        message: ticket.message,
        status: ticket.status,
        type: ticket.type,
        lastMessage: ticket.last_message || '-',
        createdAt: ticket.created_at,
        userName: authAPI.getCurrentUser()?.name || 'Ismeretlen',
        userRole: authAPI.getCurrentUser()?.role || 'user',
      }));
      
      setTickets(transformedTickets);
    } catch (err) {
      console.error('Error loading bug reports:', err);
      setError('Hiba a hibajegyek betöltésekor: ' + err.message);
    }
  };

  const handleAddTicket = async (data) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await bugReportsAPI.createBugReport(data);
      
      // Add the new ticket to the list
      const newTicket = {
        id: result.id,
        subject: result.subject,
        message: data.message,
        status: result.status,
        type: result.type,
        lastMessage: '-',
        createdAt: result.created_at,
        userName: authAPI.getCurrentUser()?.name || 'Ismeretlen',
        userRole: authAPI.getCurrentUser()?.role || 'user',
      };
      
      setTickets([newTicket, ...tickets]);
      setShowModal(false);
      
      // Reload the list to get the latest data
      await loadBugReports();
    } catch (err) {
      console.error('Error creating bug report:', err);
      setError('Hiba a hibajegy létrehozásakor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTicket = (id) => {
    setTickets(tickets => tickets.map(t => t.id === id ? { ...t, status: 'Lezárva' } : t));
    setSelectedTicket(t => t && t.id === id ? { ...t, status: 'Lezárva' } : t);
  };

  // Filter tickets based on search and status
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = !searchTerm ||
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      <div className="page-content bugreport-page">
        {selectedTicket ? (
          <div className="bugreport-detail-view">
            <button className="bugreport-back-btn" onClick={() => setSelectedTicket(null)}>
              &larr; Vissza a listához
            </button>
            <BugReportView ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onCloseTicket={handleCloseTicket} />
          </div>
        ) : (
          <div className="bugreport-layout">
            {/* Header Section */}
            <div className="bugreport-header">
              <div className="bugreport-title-section">
                <FaBug className="bugreport-page-icon" />
                <div>
                  <h2 className="bugreport-page-title">Hibajegyeim</h2>
                  <p className="bugreport-page-subtitle">
                    {tickets.length} hibajegy összesen
                  </p>
                </div>
              </div>

              <button className="bugreport-new-btn" onClick={() => setShowModal(true)}>
                <FaPlus />
                Új hibajegy
              </button>
            </div>

            {/* Search and Filter Section */}
            <div className="bugreport-controls">
              <div className="bugreport-search-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Keresés hibajegyek között..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bugreport-search-input"
                />
              </div>

              <div className="bugreport-filter-wrapper">
                <FaFilter className="filter-icon" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bugreport-filter-select"
                >
                  <option value="all">Minden státusz</option>
                  <option value="Bejelentett">Bejelentett</option>
                  <option value="Folyamatban">Folyamatban</option>
                  <option value="Megoldva">Megoldva</option>
                  <option value="Lezárva">Lezárva</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="bugreport-error-banner">
                <FaExclamationTriangle className="error-icon" />
                <div className="error-content">
                  <strong>Hiba történt:</strong> {error}
                </div>
                <button className="error-close-btn" onClick={() => setError('')}>
                  ×
                </button>
              </div>
            )}

            {/* Tickets Grid */}
            <div className="bugreport-content">
              {loading ? (
                <div className="bugreport-loading">
                  <div className="loading-spinner"></div>
                  <p>Hibajegyek betöltése...</p>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="bugreport-empty">
                  <FaBug className="empty-icon" />
                  <h3>Nincs hibajegy</h3>
                  <p>
                    {searchTerm || filterStatus !== 'all'
                      ? 'Nincs a szűrési feltételeknek megfelelő hibajegy.'
                      : 'Még nincs létrehozott hibajegy. Hozz létre az első hibajegyet!'}
                  </p>
                  {!searchTerm && filterStatus === 'all' && (
                    <button className="bugreport-empty-btn" onClick={() => setShowModal(true)}>
                      <FaPlus />
                      Első hibajegy létrehozása
                    </button>
                  )}
                </div>
              ) : (
                <div className="bugreport-grid">
                  {filteredTickets.map((ticket, idx) => (
                    <div
                      key={ticket.id || idx}
                      className="bugreport-card"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="bugreport-card-header">
                        <h4 className="bugreport-card-title">{ticket.subject}</h4>
                        {statusCell(ticket.status)}
                      </div>

                      <div className="bugreport-card-content">
                        <p className="bugreport-card-message">
                          {ticket.message?.length > 100
                            ? `${ticket.message.substring(0, 100)}...`
                            : ticket.message}
                        </p>

                        <div className="bugreport-card-meta">
                          <span className="bugreport-card-type">{ticket.type}</span>
                          <span className="bugreport-card-date">{ticket.createdAt}</span>
                        </div>
                      </div>

                      {ticket.lastMessage && ticket.lastMessage !== '-' && (
                        <div className="bugreport-card-footer">
                          <small>Utolsó üzenet: {ticket.lastMessage}</small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <BugReportModal open={showModal} onClose={() => setShowModal(false)} onSubmit={handleAddTicket} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BugReport;